import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "react-query";
import {
  useDataProvider,
  useNotify,
  SimpleForm,
  TextInput,
  SelectInput,
  PasswordInput,
  useResourceContext,
  useRefresh,
  required,
  choices,
  maxLength,
  email,
  Edit,
  useUpdate,
  BooleanInput,
} from "react-admin";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";

const passwordConfirmation = (value, allValues) => {
  if (value !== allValues.password) {
    return "Password doesn't match";
  }
  return undefined;
};
const changePassword = (value, allValues) => {
  if (typeof value === "undefined") {
    if (typeof allValues.password !== "undefined") {
      return "Current Password is required to change password";
    }
  }
  return undefined;
};

const validateCompany = [required()];
const validateFirstName = [required(), maxLength(100)];
const validateLastName = [maxLength(100)];
const validateGender = [
  choices(["Male", "Female"], "Please choose one of the values"),
];
const validateUsername = [required(), maxLength(50)];
const validateEmail = [required(), email(), maxLength(50)];
const validateRole = [required()];
const validateCurrentPassword = [changePassword];
const validatePasswordConfirmation = [passwordConfirmation];

export default function UserEditDialog({ open, setOpen, recordId }) {
  const [update] = useUpdate();
  const notify = useNotify();
  const resource = useResourceContext();
  const refresh = useRefresh();

  const dataProvider = useDataProvider();

  // GET COMPANY LISTS
  const {
    data: companies,
    isLoading: isLoadingCompanies,
    error: companyError,
  } = useQuery(["companies/select", "getListNoParams"], () =>
    dataProvider.getListNoParams("companies/select")
  );
  // GET COMPANY LISTS

  // GET ROLE LISTS
  const {
    data: roleMaster,
    isLoading: isLoadingRoles,
    error: roleError,
  } = useQuery(["user-access/master", "getListNoParams"], () =>
    dataProvider.getListNoParams("user-access/master")
  );
  // GET ROLE LISTS

  const save = useCallback(
    async (values) => {
      try {
        await update(
          resource,
          { id: values.id, data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.updated", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [update]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} keepMounted={false}>
      <Edit
        title={" "}
        id={recordId}
        actions={false}
        sx={{ "& .RaEdit-main": { mt: 0 } }}
      >
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns callback={handleClose} />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>Edit User</DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent>
            {/* <ImageDropzone /> */}
            <TextInput source="id" sx={{ display: "none" }} />

            <SelectInput
              variant="outlined"
              source="company_id"
              isRequired
              choices={isLoadingCompanies ? [] : companies}
              fullWidth
              validate={validateCompany}
            />

            <TextInput
              variant="outlined"
              source="first_name"
              fullWidth
              isRequired
              validate={validateFirstName}
            />

            <TextInput
              variant="outlined"
              source="last_name"
              fullWidth
              validate={validateLastName}
            />

            <SelectInput
              variant="outlined"
              source="gender"
              choices={[
                { id: "Male", name: "Male" },
                { id: "Female", name: "Female" },
              ]}
              fullWidth
              validate={validateGender}
            />

            <MuiDatePickerInput
              variant="outlined"
              source="birth_date"
              fullWidth
              sx={{ display: "none" }}
            />

            <TextInput
              variant="outlined"
              source="username"
              fullWidth
              isRequired
              validate={validateUsername}
            />

            <TextInput
              variant="outlined"
              source="email"
              fullWidth
              isRequired
              validate={validateEmail}
            />

            <SelectInput
              variant="outlined"
              source="role"
              isRequired
              choices={isLoadingRoles ? [] : roleMaster}
              fullWidth
              validate={validateRole}
            />

            <PasswordInput
              variant="outlined"
              source="current_password"
              fullWidth
              validate={validateCurrentPassword}
            />

            <PasswordInput variant="outlined" source="password" fullWidth />

            <PasswordInput
              variant="outlined"
              source="password_confirmation"
              fullWidth
              validate={validatePasswordConfirmation}
            />

            <BooleanInput source="active" />
          </DialogContent>
        </SimpleForm>
      </Edit>
    </Dialog>
  );
}
